function toOptionalNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function toNumber(value, fallback = 0) {
  const number = toOptionalNumber(value);
  return number === null ? fallback : number;
}

export function normalizeDemoProfile(payload) {
  const source = payload && typeof payload === "object" ? payload : {};
  const demoModeEnabled = Boolean(
    source.demo_mode_enabled ??
      source.demoModeEnabled ??
      source.demo_mode ??
      source.demoMode ??
      source.is_demo ??
      source.isDemo ??
      source.demo ??
      false
  );

  const demoProjectLimit = toOptionalNumber(
    source.demo_project_limit ??
      source.demoProjectLimit ??
      source.demo_max_projects ??
      source.demoMaxProjects ??
      source.max_demo_projects ??
      source.maxDemoProjects
  );
  const demoProjectsUsed = toOptionalNumber(
    source.demo_projects_used ??
      source.demoProjectsUsed ??
      source.demo_project_count ??
      source.demoProjectCount ??
      source.project_count ??
      source.projectCount ??
      source.projects_count ??
      source.projectsCount
  );
  const demoProjectsRemaining = toOptionalNumber(
    source.demo_projects_remaining ??
      source.demoProjectsRemaining ??
      source.demo_project_remaining ??
      source.demoProjectRemaining ??
      source.projects_remaining ??
      source.projectsRemaining ??
      source.project_remaining ??
      source.projectRemaining
  );

  return {
    demoModeEnabled,
    demoLocked: Boolean(source.demo_locked ?? source.demoLocked ?? false),
    demoRemainingDays: toNumber(source.demo_remaining_days ?? source.demoRemainingDays, 0),
    demoMarketOptimizationLimitPerDay: toNumber(
      source.demo_market_optimization_limit_per_day ??
        source.demoMarketOptimizationLimitPerDay,
      0
    ),
    demoFeasibilityLimitPerDay: toNumber(
      source.demo_feasibility_limit_per_day ?? source.demoFeasibilityLimitPerDay,
      0
    ),
    marketOptimizationRemainingToday: toNumber(
      source.market_optimization_remaining_today ??
        source.marketOptimizationRemainingToday,
      0
    ),
    feasibilityRemainingToday: toNumber(
      source.feasibility_remaining_today ?? source.feasibilityRemainingToday,
      0
    ),
    // Runs counted today (successful or still running; failed and cancelled
    // runs are given back by the backend).
    feasibilityUsedToday: toNumber(source.feasibility_used_today ?? source.feasibilityUsedToday, 0),
    marketOptimizationUsedToday: toNumber(
      source.market_optimization_used_today ?? source.marketOptimizationUsedToday,
      0
    ),
    demoProjectLimit,
    demoProjectsUsed,
    demoProjectsRemaining,
    demoCanSubmitBids: Boolean(
      source.demo_can_submit_bids ?? source.demoCanSubmitBids ?? false
    ),
    demoMarketOperationsEnabled: Boolean(
      source.demo_market_operations_enabled ?? source.demoMarketOperationsEnabled ?? false
    ),
    demoWindSystemEnabled: Boolean(
      source.demo_wind_system_enabled ?? source.demoWindSystemEnabled ?? false
    ),
    demoMarketDataEnabled: Boolean(
      source.demo_market_data_enabled ?? source.demoMarketDataEnabled ?? false
    ),
    demoMerchantEnabled: Boolean(
      source.demo_merchant_enabled ?? source.demoMerchantEnabled ?? true
    ),
    demoPpaEnabled: Boolean(
      source.demo_ppa_enabled ?? source.demoPpaEnabled ?? false
    ),
    demoTollingEnabled: Boolean(
      source.demo_tolling_enabled ?? source.demoTollingEnabled ?? false
    ),
    demoMultipleAssetsEnabled: Boolean(
      source.demo_multiple_assets_enabled ?? source.demoMultipleAssetsEnabled ?? false
    ),
    regularIsAdmin: Boolean(source.regular_is_admin ?? source.regularIsAdmin ?? false),
    regularMarketOperationsEnabled: Boolean(
      source.regular_market_operations_enabled ?? source.regularMarketOperationsEnabled ?? true
    ),
    regularCanSubmitBids: Boolean(
      source.regular_can_submit_bids ?? source.regularCanSubmitBids ?? true
    ),
    regularWindSystemEnabled: Boolean(
      source.regular_wind_system_enabled ?? source.regularWindSystemEnabled ?? true
    ),
    regularMarketDataEnabled: Boolean(
      source.regular_market_data_enabled ?? source.regularMarketDataEnabled ?? true
    ),
    regularFeasibilityEnabled: Boolean(
      source.regular_feasibility_enabled ?? source.regularFeasibilityEnabled ?? true
    ),
    regularMerchantEnabled: Boolean(
      source.regular_merchant_enabled ?? source.regularMerchantEnabled ?? true
    ),
    regularPpaEnabled: Boolean(
      source.regular_ppa_enabled ?? source.regularPpaEnabled ?? false
    ),
    regularTollingEnabled: Boolean(
      source.regular_tolling_enabled ?? source.regularTollingEnabled ?? false
    ),
    regularMonitoringEnabled: Boolean(
      source.regular_monitoring_enabled ?? source.regularMonitoringEnabled ?? false
    ),
    regularMultipleAssetsEnabled: Boolean(
      source.regular_multiple_assets_enabled ?? source.regularMultipleAssetsEnabled ?? false
    ),
    regularMarketOptimizationLimitPerDay: toOptionalNumber(
      source.regular_market_optimization_limit_per_day ?? source.regularMarketOptimizationLimitPerDay
    ),
    regularFeasibilityLimitPerDay: toOptionalNumber(
      source.regular_feasibility_limit_per_day ?? source.regularFeasibilityLimitPerDay
    ),
    regularMarketOptimizationRemainingToday: toOptionalNumber(
      source.regular_market_optimization_remaining_today ?? source.regularMarketOptimizationRemainingToday
    ),
    regularFeasibilityRemainingToday: toOptionalNumber(
      source.regular_feasibility_remaining_today ?? source.regularFeasibilityRemainingToday
    ),
    regularMaxProjects: toOptionalNumber(source.regular_max_projects ?? source.regularMaxProjects),
    regularProjectsUsed: toOptionalNumber(source.regular_projects_used ?? source.regularProjectsUsed),
    regularProjectsRemaining: toOptionalNumber(
      source.regular_projects_remaining ?? source.regularProjectsRemaining
    ),
    regularMaxSimulationDays: toOptionalNumber(
      source.regular_max_simulation_days ?? source.regularMaxSimulationDays
    ),
  };
}

/**
 * What the account has used, for the discreet usage line in the top bar.
 *
 * Admins see nothing. A demo account reads the demo limits, every other
 * account the limits of its plan. A missing limit means "no limit": only the
 * count is shown. Market optimisations appear only for accounts that have
 * Market Operations and have a limit or have run one today.
 */
export function accountUsage(profile) {
  if (!profile) return null;
  const demo = Boolean(profile.demoModeEnabled);
  if (!demo && profile.regularIsAdmin) return null;
  const positiveOrNull = (value) => (Number.isFinite(value) && value > 0 ? value : null);
  const optionalOrNull = (value) => (Number.isFinite(value) ? value : null);

  const projectsUsed = optionalOrNull(profile.demoProjectsUsed ?? profile.regularProjectsUsed ?? null);
  const projects = projectsUsed === null
    ? null
    : {
      used: projectsUsed,
      limit: demo ? positiveOrNull(profile.demoProjectLimit) : optionalOrNull(profile.regularMaxProjects),
    };
  const simulations = {
    used: Number(profile.feasibilityUsedToday) || 0,
    limit: demo
      ? positiveOrNull(profile.demoFeasibilityLimitPerDay)
      : optionalOrNull(profile.regularFeasibilityLimitPerDay),
  };
  const marketEnabled = demo ? profile.demoMarketOperationsEnabled : profile.regularMarketOperationsEnabled;
  const optimizationLimit = demo
    ? positiveOrNull(profile.demoMarketOptimizationLimitPerDay)
    : optionalOrNull(profile.regularMarketOptimizationLimitPerDay);
  const optimizationUsed = Number(profile.marketOptimizationUsedToday) || 0;
  const optimizations = marketEnabled && (optimizationLimit !== null || optimizationUsed > 0)
    ? { used: optimizationUsed, limit: optimizationLimit }
    : null;
  const reached = (item) => Boolean(item && item.limit !== null && item.used >= item.limit);
  return {
    projects,
    simulations,
    optimizations,
    limitReached: reached(projects) || reached(simulations) || reached(optimizations),
  };
}

export function mergeDemoProfiles(...profiles) {
  const normalized = profiles
    .filter(Boolean)
    .map((profile) => normalizeDemoProfile(profile));
  if (!normalized.length) return normalizeDemoProfile(null);

  const pickNumber = (key) => {
    for (let index = normalized.length - 1; index >= 0; index -= 1) {
      const value = normalized[index]?.[key];
      if (value !== null && value !== undefined) return value;
    }
    return null;
  };

  return {
    demoModeEnabled: normalized.some((profile) => profile.demoModeEnabled),
    demoLocked: normalized.some((profile) => profile.demoLocked),
    demoRemainingDays: pickNumber("demoRemainingDays") ?? 0,
    demoMarketOptimizationLimitPerDay: pickNumber("demoMarketOptimizationLimitPerDay") ?? 0,
    demoFeasibilityLimitPerDay: pickNumber("demoFeasibilityLimitPerDay") ?? 0,
    marketOptimizationRemainingToday: pickNumber("marketOptimizationRemainingToday") ?? 0,
    feasibilityRemainingToday: pickNumber("feasibilityRemainingToday") ?? 0,
    feasibilityUsedToday: pickNumber("feasibilityUsedToday") ?? 0,
    marketOptimizationUsedToday: pickNumber("marketOptimizationUsedToday") ?? 0,
    demoProjectLimit: pickNumber("demoProjectLimit"),
    demoProjectsUsed: pickNumber("demoProjectsUsed"),
    demoProjectsRemaining: pickNumber("demoProjectsRemaining"),
    demoCanSubmitBids: Boolean(pickNumber("demoCanSubmitBids")),
    demoMarketOperationsEnabled: Boolean(pickNumber("demoMarketOperationsEnabled")),
    demoWindSystemEnabled: Boolean(pickNumber("demoWindSystemEnabled")),
    demoMarketDataEnabled: Boolean(pickNumber("demoMarketDataEnabled")),
    demoMerchantEnabled: Boolean(pickNumber("demoMerchantEnabled")),
    demoPpaEnabled: Boolean(pickNumber("demoPpaEnabled")),
    demoTollingEnabled: Boolean(pickNumber("demoTollingEnabled")),
    demoMultipleAssetsEnabled: Boolean(pickNumber("demoMultipleAssetsEnabled")),
    regularIsAdmin: Boolean(pickNumber("regularIsAdmin")),
    regularMarketOperationsEnabled: Boolean(pickNumber("regularMarketOperationsEnabled")),
    regularCanSubmitBids: Boolean(pickNumber("regularCanSubmitBids")),
    regularWindSystemEnabled: Boolean(pickNumber("regularWindSystemEnabled")),
    regularMarketDataEnabled: Boolean(pickNumber("regularMarketDataEnabled")),
    regularFeasibilityEnabled: Boolean(pickNumber("regularFeasibilityEnabled")),
    regularMerchantEnabled: Boolean(pickNumber("regularMerchantEnabled")),
    regularPpaEnabled: Boolean(pickNumber("regularPpaEnabled")),
    regularTollingEnabled: Boolean(pickNumber("regularTollingEnabled")),
    regularMonitoringEnabled: Boolean(pickNumber("regularMonitoringEnabled")),
    regularMultipleAssetsEnabled: Boolean(pickNumber("regularMultipleAssetsEnabled")),
    regularMarketOptimizationLimitPerDay: pickNumber("regularMarketOptimizationLimitPerDay"),
    regularFeasibilityLimitPerDay: pickNumber("regularFeasibilityLimitPerDay"),
    regularMarketOptimizationRemainingToday: pickNumber("regularMarketOptimizationRemainingToday"),
    regularFeasibilityRemainingToday: pickNumber("regularFeasibilityRemainingToday"),
    regularMaxProjects: pickNumber("regularMaxProjects"),
    regularProjectsUsed: pickNumber("regularProjectsUsed"),
    regularProjectsRemaining: pickNumber("regularProjectsRemaining"),
    regularMaxSimulationDays: pickNumber("regularMaxSimulationDays"),
  };
}

export function isDemoModeEnabled(demoProfile) {
  return Boolean(demoProfile?.demoModeEnabled);
}

export function isDemoExpired(demoProfile) {
  if (!isDemoModeEnabled(demoProfile)) return false;
  return Number(demoProfile?.demoRemainingDays ?? 0) <= 0;
}

export function getDemoBaseRestrictionMessage(demoProfile) {
  if (!isDemoModeEnabled(demoProfile)) return "";
  if (demoProfile?.demoLocked) return "This demo account is locked.";
  if (isDemoExpired(demoProfile)) return "Your demo period has expired.";
  return "";
}

export function getDemoActionDisabledMessage(demoProfile, quota) {
  const baseMessage = getDemoBaseRestrictionMessage(demoProfile);
  if (baseMessage) return baseMessage;
  if (!isDemoModeEnabled(demoProfile)) {
    if (demoProfile?.regularIsAdmin) return "";
    if (quota === "market") {
      if (!demoProfile?.regularMarketOperationsEnabled) return "Market Operations are disabled for this account.";
      if (demoProfile?.regularMarketOptimizationRemainingToday !== null &&
          demoProfile?.regularMarketOptimizationRemainingToday !== undefined &&
          Number(demoProfile.regularMarketOptimizationRemainingToday) <= 0)
        return "You have reached today's Market Operations run limit.";
    }
    if (quota === "feasibility") {
      if (!demoProfile?.regularFeasibilityEnabled) return "Feasibility is disabled for this account.";
      if (demoProfile?.regularFeasibilityRemainingToday !== null &&
          demoProfile?.regularFeasibilityRemainingToday !== undefined &&
          Number(demoProfile.regularFeasibilityRemainingToday) <= 0)
        return "You have reached today's Feasibility run limit.";
    }
    return "";
  }

  if (quota === "market") {
    return Number(demoProfile?.marketOptimizationRemainingToday ?? 0) <= 0
      ? "You have reached today's demo run limit."
      : "";
  }

  if (quota === "feasibility") {
    return Number(demoProfile?.feasibilityRemainingToday ?? 0) <= 0
      ? "You have reached today's demo feasibility run limit."
      : "";
  }

  return "";
}

export function getDemoExportDisabledMessage(demoProfile, scope) {
  if (scope === "feasibility" && !isDemoModeEnabled(demoProfile) && !demoProfile?.regularIsAdmin &&
      demoProfile?.regularFeasibilityEnabled === false)
    return "Feasibility is disabled for this account.";
  return getDemoBaseRestrictionMessage(demoProfile);
}

export function getDemoBidWriteDisabledMessage(demoProfile) {
  if (!isDemoModeEnabled(demoProfile)) {
    if (demoProfile?.regularIsAdmin) return "";
    if (demoProfile?.regularMarketOperationsEnabled === false)
      return "Market Operations are disabled for this account.";
    return demoProfile?.regularCanSubmitBids === false
      ? "Bid schedule actions are disabled for this account." : "";
  }
  const baseMessage = getDemoBaseRestrictionMessage(demoProfile);
  if (baseMessage) return baseMessage;
  if (!demoProfile?.demoMarketOperationsEnabled)
    return "Market Operations are disabled for this demo account.";
  if (demoProfile?.demoCanSubmitBids) return "";
  return "Bid schedule write actions are disabled for demo accounts.";
}

export function getDemoMarketOperationsDisabledMessage(demoProfile) {
  if (!isDemoModeEnabled(demoProfile))
    return !demoProfile?.regularIsAdmin && demoProfile?.regularMarketOperationsEnabled === false
      ? "Market Operations are disabled for this account." : "";
  const baseMessage = getDemoBaseRestrictionMessage(demoProfile);
  if (baseMessage) return baseMessage;
  if (demoProfile?.demoMarketOperationsEnabled) return "";
  return "Market Operations are disabled for this demo account.";
}

export function getDemoWindSystemDisabledMessage(demoProfile) {
  if (!isDemoModeEnabled(demoProfile))
    return !demoProfile?.regularIsAdmin && demoProfile?.regularWindSystemEnabled === false
      ? "Wind System is disabled for this account." : "";
  const baseMessage = getDemoBaseRestrictionMessage(demoProfile);
  if (baseMessage) return baseMessage;
  if (demoProfile?.demoWindSystemEnabled) return "";
  return "Wind System is disabled for this demo account.";
}

export function getDemoMarketDataDisabledMessage(demoProfile) {
  if (!isDemoModeEnabled(demoProfile))
    return !demoProfile?.regularIsAdmin && demoProfile?.regularMarketDataEnabled === false
      ? "Market Data is disabled for this account." : "";
  const baseMessage = getDemoBaseRestrictionMessage(demoProfile);
  if (baseMessage) return baseMessage;
  if (demoProfile?.demoMarketDataEnabled) return "";
  return "Market Data is disabled for this demo account.";
}

const TOLLING_SETTLEMENT_TYPES = new Set([
  "TollingFixed",
  "TollingFloorUpside",
  "HybridPartial",
]);

export function getRevenueModelDisabledMessage(demoProfile, settlementType) {
  const type = String(settlementType || "").trim();
  const isMerchant = type === "Arbitrage";
  const isPpa = type === "PPA";
  const isTolling = TOLLING_SETTLEMENT_TYPES.has(type);
  if (!isMerchant && !isPpa && !isTolling) return "";
  if (!demoProfile) return "Account permissions are still loading.";
  if (demoProfile.regularIsAdmin) return "";

  const prefix = isDemoModeEnabled(demoProfile) ? "demo" : "regular";
  const enabled = isMerchant
    ? demoProfile[`${prefix}MerchantEnabled`]
    : isPpa
      ? demoProfile[`${prefix}PpaEnabled`]
      : demoProfile[`${prefix}TollingEnabled`];
  if (enabled) return "";
  if (isMerchant) return "The Merchant revenue model is not enabled for this account.";
  if (isPpa) return "The PPA revenue model is not enabled for this account.";
  return "Tolling revenue models are not enabled for this account.";
}

export function getRegularFeasibilityDisabledMessage(demoProfile) {
  if (isDemoModeEnabled(demoProfile) || demoProfile?.regularIsAdmin) return "";
  return demoProfile?.regularFeasibilityEnabled === false
    ? "Feasibility is disabled for this account." : "";
}

export function getMonitoringDisabledMessage(demoProfile) {
  if (!demoProfile) return "Account permissions are still loading.";
  if (demoProfile.regularIsAdmin) return "";
  if (isDemoModeEnabled(demoProfile)) {
    return "Asset Monitoring is not enabled for demo accounts.";
  }
  return demoProfile.regularMonitoringEnabled
    ? ""
    : "Asset Monitoring is not enabled for this account.";
}

export function getMultipleAssetsDisabledMessage(demoProfile) {
  if (!demoProfile) return "Account permissions are still loading.";
  if (demoProfile.regularIsAdmin) return "";
  if (isDemoModeEnabled(demoProfile)) {
    const baseMessage = getDemoBaseRestrictionMessage(demoProfile);
    if (baseMessage) return baseMessage;
    return demoProfile.demoMultipleAssetsEnabled
      ? ""
      : "Multiple assets per project are not enabled for this demo account.";
  }
  return demoProfile.regularMultipleAssetsEnabled
    ? ""
    : "Multiple assets per project are not enabled for this account.";
}

export function getDemoProjectCreateDisabledMessage(demoProfile) {
  const baseMessage = getDemoBaseRestrictionMessage(demoProfile);
  if (baseMessage) return baseMessage;
  if (!isDemoModeEnabled(demoProfile)) {
    if (!demoProfile?.regularIsAdmin && demoProfile?.regularProjectsRemaining !== null &&
        demoProfile?.regularProjectsRemaining !== undefined &&
        Number(demoProfile.regularProjectsRemaining) <= 0)
      return "Project limit reached for this account.";
    return "";
  }

  if (demoProfile?.demoProjectsRemaining !== null && demoProfile?.demoProjectsRemaining !== undefined) {
    return Number(demoProfile.demoProjectsRemaining) <= 0 ? "Demo project limit reached." : "";
  }

  if (
    demoProfile?.demoProjectLimit !== null &&
    demoProfile?.demoProjectLimit !== undefined &&
    demoProfile?.demoProjectsUsed !== null &&
    demoProfile?.demoProjectsUsed !== undefined
  ) {
    return Number(demoProfile.demoProjectsUsed) >= Number(demoProfile.demoProjectLimit)
      ? "Demo project limit reached."
      : "";
  }

  return "";
}

// Custom PV modules, inverters and batteries: administrators and paid accounts
// (added to their own organisation's catalogue); never demo accounts. The
// backend enforces the same rule (catalog.get_current_catalog_writer_user_id).
export function getCatalogWriteDisabledMessage({ isAdminUser, demoProfile, accessLoading, demoStatusKnown = true }) {
  if (accessLoading) return "Checking account permissions...";
  if (!demoStatusKnown) return "Custom catalog entries are unavailable until demo status is verified.";
  if (isDemoModeEnabled(demoProfile)) return "Custom equipment cannot be added from a demo account.";
  if (isAdminUser) return "";
  if (demoProfile?.regularFeasibilityEnabled === false) return "Feasibility is disabled for this account.";
  return "";
}

// Custom wind turbines stay administrator-only.
export function getTurbineWriteDisabledMessage({ isAdminUser, demoProfile, accessLoading, demoStatusKnown = true }) {
  if (accessLoading) return "Checking account permissions...";
  if (!demoStatusKnown) return "Custom catalog entries are unavailable until demo status is verified.";
  if (isDemoModeEnabled(demoProfile)) return "Custom catalog entries are disabled for demo accounts.";
  if (!isAdminUser) return "Custom turbines can be added by administrators only.";
  return "";
}
